/**
 * ============================================================================
 * PayoutsTable - FULL PAYOUT HISTORY TABLE WITH PAGINATION
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. PURE PRESENTATION COMPONENT
 *    - Receives filtered data via props
 *    - No filtering logic here
 *    - Parent handles all data transformations
 *
 * 2. FEATURES
 *    - Sortable columns (date, amount, status)
 *    - Pagination (10 items per page)
 *    - Row click opens detail modal
 *    - Status badges with colors
 *
 * 3. COLUMNS
 *    - Date
 *    - Transfer Reference
 *    - Amount (net_payout)
 *    - Bookings Count
 *    - Status (badge)
 *    - Actions (view details)
 *
 * 4. RESPONSIVE
 *    - Mobile: Stacked card layout
 *    - Desktop: Full table
 *
 * ============================================================================
 */

import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Eye, CheckCircle, Clock, XCircle, AlertCircle, Search, Filter, Download } from 'lucide-react';
import { formatCurrency, formatDate, getPayoutStatusColor } from '../../utils/revenueFormatters';
import type { PayoutHistory } from '../../types/revenue';

interface PayoutsTableProps {
  payouts: PayoutHistory[];
  onPayoutClick: (payout: PayoutHistory) => void;
  onExport: () => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
}

/**
 * Payouts table with pagination, sorting, and search
 */
export function PayoutsTable({ payouts, onPayoutClick, onExport, statusFilter, onStatusFilterChange }: PayoutsTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'date' | 'amount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');

  const itemsPerPage = 10;

  // Search and sort payouts
  const filteredAndSortedPayouts = useMemo(() => {
    // First filter by search query
    let filtered = payouts;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = payouts.filter(
        (p) =>
          p.transfer_reference.toLowerCase().includes(query) ||
          p.status.toLowerCase().includes(query) ||
          (p.transfer_code && p.transfer_code.toLowerCase().includes(query))
      );
    }

    // Then sort
    return [...filtered].sort((a, b) => {
      if (sortBy === 'date') {
        const comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        return sortOrder === 'asc' ? comparison : -comparison;
      } else {
        const comparison = a.net_payout - b.net_payout;
        return sortOrder === 'asc' ? comparison : -comparison;
      }
    });
  }, [payouts, searchQuery, sortBy, sortOrder]);

  // Paginate
  const totalPages = Math.ceil(filteredAndSortedPayouts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPayouts = filteredAndSortedPayouts.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleSort = (column: 'date' | 'amount') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  if (payouts.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600 font-semibold">No payouts found</p>
        <p className="text-sm text-gray-500 mt-1">Adjust your filters or check back later</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Table Header with Title, Search, Filter, and Export */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Payout History</h2>
            <p className="text-sm text-gray-600">{filteredAndSortedPayouts.length} total payouts</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold bg-white text-gray-700 outline-none focus:border-blue-500 appearance-none"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="processing">Processing</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            {/* Export Button */}
            <button
              onClick={onExport}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors text-sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by reference..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('date')}
              >
                Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Reference
              </th>
              <th
                className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('amount')}
              >
                Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Bookings
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {paginatedPayouts.map((payout) => {
              const statusColorClass = getPayoutStatusColor(payout.status);

              return (
                <tr
                  key={payout.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => onPayoutClick(payout)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="font-semibold text-gray-900">{formatDate(payout.created_at)}</p>
                    {payout.completed_at && (
                      <p className="text-xs text-gray-500">Completed: {formatDate(payout.completed_at)}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="font-mono text-sm text-gray-900">{payout.transfer_reference}</p>
                    {payout.transfer_code && (
                      <p className="text-xs text-gray-500 font-mono">{payout.transfer_code}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="font-black text-gray-900">{formatCurrency(payout.net_payout)}</p>
                    {payout.gross_revenue && (
                      <p className="text-xs text-gray-500">
                        Gross: {formatCurrency(payout.gross_revenue)}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="font-semibold text-gray-900">
                      {payout.booking_count} bookings
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(payout.status)}
                      <span className={`px-2 py-1 rounded-lg text-xs font-bold ${statusColorClass}`}>
                        {payout.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPayoutClick(payout);
                      }}
                      className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center justify-end ml-auto"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-gray-200">
        {paginatedPayouts.map((payout) => {
          const statusColorClass = getPayoutStatusColor(payout.status);

          return (
            <div
              key={payout.id}
              className="p-4 hover:bg-gray-50 cursor-pointer"
              onClick={() => onPayoutClick(payout)}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-black text-gray-900">{formatCurrency(payout.net_payout)}</p>
                  <p className="text-xs text-gray-500">{formatDate(payout.created_at)}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(payout.status)}
                  <span className={`px-2 py-1 rounded-lg text-xs font-bold ${statusColorClass}`}>
                    {payout.status}
                  </span>
                </div>
              </div>
              <p className="text-sm font-mono text-gray-600 mb-1">{payout.transfer_reference}</p>
              <p className="text-xs text-gray-500">
                {payout.booking_count} bookings
              </p>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredAndSortedPayouts.length)} of{' '}
            {filteredAndSortedPayouts.length} payouts
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
